import { api } from '@/lib/api';
import { useSession } from '@/lib/session';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@iq/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Search, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type FeedbackEvent = NonNullable<Awaited<ReturnType<typeof fetchFeedback>>>['events'][number];
type AdminUser = NonNullable<Awaited<ReturnType<typeof fetchUsers>>>['users'][number];
type FeedbackStatus = 'new' | 'reviewed' | 'kb_updated' | 'duplicate' | 'rejected';
type FeedbackType = 'knowledge_gap' | 'partial_knowledge_gap' | 'bad_answer';
type FeedbackTab = 'all' | FeedbackType;
type StatusFilter = 'all' | FeedbackStatus;
type AssigneeFilter = 'all' | 'unassigned' | string;
type FeedbackSource = {
  name: string;
  type?: string;
  kbId?: string;
  documentId?: string;
  ref?: string;
};

const feedbackTabs: FeedbackTab[] = ['all', 'knowledge_gap', 'partial_knowledge_gap', 'bad_answer'];
const statuses: FeedbackStatus[] = ['new', 'reviewed', 'kb_updated', 'duplicate', 'rejected'];

async function fetchFeedback() {
  const { data, error } = await api.GET('/admin/feedback');
  if (error) throw error;
  return data;
}

async function fetchUsers() {
  const { data, error } = await api.GET('/admin/users');
  if (error) throw error;
  return data;
}

function userLabel(user: { email: string; name?: string | null }) {
  return user.name ? `${user.name} (${user.email})` : user.email;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function sourceDocumentsForEvent(event: FeedbackEvent): FeedbackSource[] {
  if (event.sourceDocuments?.length) return event.sourceDocuments;
  return event.sourceDocumentTitles?.map((title) => ({ name: title })) ?? [];
}

function includesQuery(event: FeedbackEvent, query: string) {
  if (!query) return true;
  const haystack = [
    event.question,
    event.rewrittenQuery,
    event.answerText,
    event.feedbackText,
    event.feedbackReason,
    event.assignee?.name,
    event.assignee?.email,
    event.category,
    event.productArea,
    ...(event.searchKeywords ?? []),
    ...(event.sourceDocumentTitles ?? []),
    ...(event.sourceDocuments ?? []).flatMap((source) => [
      source.name,
      source.type,
      source.kbId,
      source.documentId,
      source.ref,
    ]),
  ]
    .filter(Boolean)
    .join('\n')
    .toLocaleLowerCase();
  return haystack.includes(query);
}

export function FeedbackTable() {
  const { t } = useTranslation();
  const session = useSession();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<FeedbackTab>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<FeedbackEvent | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkStatus, setBulkStatus] = useState<FeedbackStatus>('reviewed');
  const [deleteSelectedOpen, setDeleteSelectedOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupStatus, setCleanupStatus] = useState<FeedbackStatus>('kb_updated');
  const isAdmin = session.status === 'authenticated' && session.session.role === 'admin';

  const feedbackQuery = useQuery({
    queryKey: ['admin', 'feedback'],
    queryFn: fetchFeedback,
  });

  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: fetchUsers,
  });

  const updateStatus = useMutation({
    mutationFn: async ({ eventId, status }: { eventId: string; status: FeedbackStatus }) => {
      const { data, error } = await api.PATCH('/admin/feedback/{eventId}/status', {
        params: { path: { eventId } },
        body: { status },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (updated) => {
      if (selectedEvent?.id === updated.id) setSelectedEvent(updated);
      return queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    },
  });

  const updateMetadata = useMutation({
    mutationFn: async ({
      eventId,
      category,
      productArea,
      searchKeywords,
    }: {
      eventId: string;
      category: string | null;
      productArea: string | null;
      searchKeywords: string[] | null;
    }) => {
      const { data, error } = await api.PATCH('/admin/feedback/{eventId}/metadata', {
        params: { path: { eventId } },
        body: { category, productArea, searchKeywords },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (updated) => {
      if (selectedEvent?.id === updated.id) setSelectedEvent(updated);
      return queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    },
  });

  const updateAssignee = useMutation({
    mutationFn: async ({ eventId, userId }: { eventId: string; userId: string | null }) => {
      const { data, error } = await api.PATCH('/admin/feedback/{eventId}/assignee', {
        params: { path: { eventId } },
        body: { userId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (updated) => {
      if (selectedEvent?.id === updated.id) setSelectedEvent(updated);
      return queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    },
  });

  const deleteByStatus = useMutation({
    mutationFn: async (status: FeedbackStatus) => {
      const { data, error } = await api.POST('/admin/feedback/delete-by-status', {
        body: { statuses: [status] },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setCleanupOpen(false);
      return queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    },
  });

  const bulkUpdateStatus = useMutation({
    mutationFn: async ({ eventIds, status }: { eventIds: string[]; status: FeedbackStatus }) => {
      const { data, error } = await api.PATCH('/admin/feedback/status', {
        body: { eventIds, status },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setSelectedIds(new Set());
      return queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    },
  });

  const deleteSelected = useMutation({
    mutationFn: async (eventIds: string[]) => {
      const { data, error } = await api.POST('/admin/feedback/delete-by-ids', {
        body: { eventIds },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setDeleteSelectedOpen(false);
      setSelectedIds(new Set());
      return queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    },
  });

  const events = feedbackQuery.data?.events ?? [];
  const users = usersQuery.data?.users ?? [];
  const countsByType = useMemo(() => {
    return events.reduce<Record<FeedbackTab, number>>(
      (acc, event) => {
        acc.all += 1;
        acc[event.type] += 1;
        return acc;
      },
      { all: 0, knowledge_gap: 0, partial_knowledge_gap: 0, bad_answer: 0 },
    );
  }, [events]);
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const filteredEvents = useMemo(
    () =>
      events.filter(
        (event) =>
          (activeTab === 'all' || event.type === activeTab) &&
          (statusFilter === 'all' || event.status === statusFilter) &&
          (assigneeFilter === 'all' ||
            (assigneeFilter === 'unassigned'
              ? !event.assignee
              : event.assignee?.id === assigneeFilter)) &&
          includesQuery(event, normalizedSearch),
      ),
    [activeTab, assigneeFilter, events, normalizedSearch, statusFilter],
  );
  const visibleIds = useMemo(() => filteredEvents.map((event) => event.id), [filteredEvents]);
  const selectedVisibleCount = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;

  const toggleSelected = (eventId: string, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) next.add(eventId);
      else next.delete(eventId);
      return next;
    });
  };

  const toggleVisibleSelected = (checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const id of visibleIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  if (feedbackQuery.isPending) return <Skeleton className="h-64 w-full" />;
  if (feedbackQuery.isError || !feedbackQuery.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('admin.feedback.error')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5" data-testid="admin-feedback-table">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('admin.feedback.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('admin.feedback.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {isAdmin ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setCleanupOpen(true)}>
              <Trash2 className="mr-2 size-4" />
              {t('admin.feedback.cleanup.openButton')}
            </Button>
          ) : null}
          <Badge variant="secondary">
            {t('admin.feedback.totalCount', { count: feedbackQuery.data.total })}
          </Badge>
        </div>
      </header>

      {deleteByStatus.data ? (
        <p className="text-sm text-muted-foreground">
          {t('admin.feedback.cleanup.deleted', { count: deleteByStatus.data.deleted })}
        </p>
      ) : null}

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as FeedbackTab)}>
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-muted/60 p-1 sm:w-auto">
          {feedbackTabs.map((tab) => (
            <TabsTrigger key={tab} value={tab} className="gap-2">
              {t(`admin.feedback.tabs.${tab}`)}
              <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                {countsByType[tab]}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <section className="grid gap-3 rounded-xl border bg-card/40 p-3 md:grid-cols-[minmax(0,1fr)_220px_260px]">
        <div className="space-y-1.5">
          <Label htmlFor="feedback-search">{t('admin.feedback.filters.search')}</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="feedback-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('admin.feedback.filters.searchPlaceholder')}
              className="pl-9"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>{t('admin.feedback.filters.status')}</Label>
          <Select
            value={statusFilter}
            onValueChange={(value) => setStatusFilter(value as StatusFilter)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin.feedback.filters.allStatuses')}</SelectItem>
              {statuses.map((status) => (
                <SelectItem key={status} value={status}>
                  {t(`admin.feedback.status.${status}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{t('admin.feedback.filters.assignee')}</Label>
          <Select
            value={assigneeFilter}
            onValueChange={(value) => setAssigneeFilter(value as AssigneeFilter)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin.feedback.filters.allAssignees')}</SelectItem>
              <SelectItem value="unassigned">{t('admin.feedback.assignee.unassigned')}</SelectItem>
              {users.map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {userLabel(user)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      <div className="text-sm text-muted-foreground">
        {t('admin.feedback.filteredCount', {
          count: filteredEvents.length,
          total: feedbackQuery.data.total,
        })}
      </div>

      <section className="flex flex-col gap-3 rounded-xl border bg-card/40 p-3 sm:flex-row sm:items-center sm:justify-between">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            ref={(input) => {
              if (input) input.indeterminate = selectedVisibleCount > 0 && !allVisibleSelected;
            }}
            onChange={(event) => toggleVisibleSelected(event.target.checked)}
          />
          {t('admin.feedback.bulk.selectVisible', { count: visibleIds.length })}
        </label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <span className="text-sm text-muted-foreground">
            {t('admin.feedback.bulk.selectedCount', { count: selectedIds.size })}
          </span>
          <Select
            value={bulkStatus}
            onValueChange={(value) => setBulkStatus(value as FeedbackStatus)}
          >
            <SelectTrigger className="w-full sm:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statuses.map((status) => (
                <SelectItem key={status} value={status}>
                  {t(`admin.feedback.status.${status}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            disabled={selectedIds.size === 0 || bulkUpdateStatus.isPending}
            onClick={() =>
              bulkUpdateStatus.mutate({ eventIds: [...selectedIds], status: bulkStatus })
            }
          >
            {t('admin.feedback.bulk.applyStatus')}
          </Button>
          {isAdmin ? (
            <Button
              type="button"
              variant="destructive"
              disabled={selectedIds.size === 0 || deleteSelected.isPending}
              onClick={() => setDeleteSelectedOpen(true)}
            >
              <Trash2 className="mr-2 size-4" />
              {t('admin.feedback.bulk.deleteSelected')}
            </Button>
          ) : null}
        </div>
      </section>

      {bulkUpdateStatus.data ? (
        <p className="text-sm text-muted-foreground">
          {t('admin.feedback.bulk.updated', { count: bulkUpdateStatus.data.updated })}
        </p>
      ) : null}
      {deleteSelected.data ? (
        <p className="text-sm text-muted-foreground">
          {t('admin.feedback.bulk.deleted', { count: deleteSelected.data.deleted })}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-xl border">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-9 px-3">
                <span className="sr-only">{t('admin.feedback.bulk.select')}</span>
              </TableHead>
              <TableHead className="w-[7.25rem] px-3">{t('admin.feedback.columns.type')}</TableHead>
              <TableHead className="px-3">{t('admin.feedback.columns.need')}</TableHead>
              <TableHead className="hidden w-36 px-3 2xl:table-cell">
                {t('admin.feedback.columns.reason')}
              </TableHead>
              <TableHead className="w-44 px-3 lg:w-52">
                {t('admin.feedback.columns.assignee')}
              </TableHead>
              <TableHead className="hidden w-32 px-3 2xl:table-cell">
                {t('admin.feedback.columns.created')}
              </TableHead>
              <TableHead className="w-32 px-3">{t('admin.feedback.columns.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEvents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center text-sm text-muted-foreground">
                  {t('admin.feedback.empty')}
                </TableCell>
              </TableRow>
            ) : (
              filteredEvents.map((event) => (
                <TableRow
                  key={event.id}
                  className="cursor-pointer align-top transition-colors hover:bg-muted/50"
                  onClick={() => setSelectedEvent(event)}
                >
                  <TableCell className="px-3 py-4" onClick={(event) => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(event.id)}
                      aria-label={t('admin.feedback.bulk.selectRow')}
                      onChange={(inputEvent) => toggleSelected(event.id, inputEvent.target.checked)}
                    />
                  </TableCell>
                  <TableCell className="px-3 py-4">
                    <Badge
                      variant={event.type === 'bad_answer' ? 'secondary' : 'outline'}
                      className="h-auto max-w-full whitespace-normal text-left leading-tight"
                    >
                      {t(`admin.feedback.types.${event.type}`)}
                    </Badge>
                  </TableCell>
                  <TableCell className="min-w-0 px-3 py-4">
                    <div className="flex flex-col gap-1">
                      <p className="line-clamp-2 break-words text-sm font-medium [overflow-wrap:anywhere]">
                        {event.rewrittenQuery ?? event.question}
                      </p>
                      {event.answerText ? (
                        <p className="line-clamp-2 break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">
                          {event.answerText}
                        </p>
                      ) : null}
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground 2xl:hidden">
                        <span>{formatDate(event.createdAt)}</span>
                        <span>
                          {event.feedbackReason
                            ? t(`admin.feedback.reasons.${event.feedbackReason}`)
                            : '—'}
                        </span>
                      </div>
                      {event.feedbackText ? (
                        <p className="line-clamp-1 break-words rounded-md bg-muted px-2 py-1 text-xs [overflow-wrap:anywhere]">
                          {event.feedbackText}
                        </p>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="hidden px-3 py-4 text-sm text-muted-foreground 2xl:table-cell">
                    {event.feedbackReason
                      ? t(`admin.feedback.reasons.${event.feedbackReason}`)
                      : '—'}
                  </TableCell>
                  <TableCell className="px-3 py-4" onClick={(event) => event.stopPropagation()}>
                    <AssigneeSelect
                      users={users}
                      value={event.assignee?.id ?? null}
                      disabled={usersQuery.isPending || updateAssignee.isPending}
                      onChange={(userId) => updateAssignee.mutate({ eventId: event.id, userId })}
                    />
                  </TableCell>
                  <TableCell className="hidden whitespace-nowrap px-3 py-4 text-sm text-muted-foreground 2xl:table-cell">
                    {formatDate(event.createdAt)}
                  </TableCell>
                  <TableCell className="px-3 py-4" onClick={(event) => event.stopPropagation()}>
                    <StatusSelect
                      value={event.status}
                      onChange={(status) => updateStatus.mutate({ eventId: event.id, status })}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <FeedbackDetailsDialog
        event={selectedEvent}
        onOpenChange={(open) => {
          if (!open) setSelectedEvent(null);
        }}
        onStatusChange={(status) => {
          if (!selectedEvent) return;
          updateStatus.mutate({ eventId: selectedEvent.id, status });
        }}
        onAssigneeChange={(userId) => {
          if (!selectedEvent) return;
          updateAssignee.mutate({ eventId: selectedEvent.id, userId });
        }}
        onMetadataSave={(input) => {
          if (!selectedEvent) return;
          updateMetadata.mutate({ eventId: selectedEvent.id, ...input });
        }}
        users={users}
        assigneePending={updateAssignee.isPending}
        metadataPending={updateMetadata.isPending}
      />

      <Dialog open={cleanupOpen} onOpenChange={setCleanupOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.feedback.cleanup.confirmTitle')}</DialogTitle>
            <DialogDescription>{t('admin.feedback.cleanup.hint')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>{t('admin.feedback.cleanup.status')}</Label>
            <Select
              value={cleanupStatus}
              onValueChange={(value) => setCleanupStatus(value as FeedbackStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {t(`admin.feedback.status.${status}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {t('admin.feedback.cleanup.confirmBody', {
                status: t(`admin.feedback.status.${cleanupStatus}`),
              })}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCleanupOpen(false)}>
              {t('admin.feedback.cleanup.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteByStatus.isPending}
              onClick={() => deleteByStatus.mutate(cleanupStatus)}
            >
              <Trash2 className="mr-2 size-4" />
              {t('admin.feedback.cleanup.confirmDelete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteSelectedOpen} onOpenChange={setDeleteSelectedOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.feedback.bulk.deleteConfirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('admin.feedback.bulk.deleteConfirmBody', { count: selectedIds.size })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteSelectedOpen(false)}>
              {t('admin.feedback.cleanup.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteSelected.isPending}
              onClick={() => deleteSelected.mutate([...selectedIds])}
            >
              <Trash2 className="mr-2 size-4" />
              {t('admin.feedback.cleanup.confirmDelete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusSelect({
  value,
  onChange,
}: { value: FeedbackStatus; onChange: (status: FeedbackStatus) => void }) {
  const { t } = useTranslation();
  return (
    <Select value={value} onValueChange={(status) => onChange(status as FeedbackStatus)}>
      <SelectTrigger className="w-full min-w-0" aria-label={t('admin.feedback.columns.status')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {statuses.map((status) => (
          <SelectItem key={status} value={status}>
            {t(`admin.feedback.status.${status}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function AssigneeSelect({
  users,
  value,
  disabled,
  onChange,
}: {
  users: AdminUser[];
  value: string | null;
  disabled?: boolean;
  onChange: (userId: string | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <Select
      value={value ?? 'unassigned'}
      disabled={disabled ?? false}
      onValueChange={(nextValue) => onChange(nextValue === 'unassigned' ? null : nextValue)}
    >
      <SelectTrigger className="w-full min-w-0" aria-label={t('admin.feedback.columns.assignee')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="unassigned">{t('admin.feedback.assignee.unassigned')}</SelectItem>
        {users.map((user) => (
          <SelectItem key={user.id} value={user.id}>
            {userLabel(user)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FeedbackDetailsDialog({
  event,
  onOpenChange,
  onStatusChange,
  onAssigneeChange,
  onMetadataSave,
  users,
  assigneePending,
  metadataPending,
}: {
  event: FeedbackEvent | null;
  onOpenChange: (open: boolean) => void;
  onStatusChange: (status: FeedbackStatus) => void;
  onAssigneeChange: (userId: string | null) => void;
  onMetadataSave: (input: {
    category: string | null;
    productArea: string | null;
    searchKeywords: string[] | null;
  }) => void;
  users: AdminUser[];
  assigneePending: boolean;
  metadataPending: boolean;
}) {
  const { t } = useTranslation();
  const [category, setCategory] = useState('');
  const [productArea, setProductArea] = useState('');
  const [searchKeywords, setSearchKeywords] = useState('');

  useEffect(() => {
    if (!event) return;
    setCategory(event.category ?? '');
    setProductArea(event.productArea ?? '');
    setSearchKeywords((event.searchKeywords ?? []).join(', '));
  }, [event]);

  const saveMetadata = () => {
    const keywords = searchKeywords
      .split(',')
      .map((keyword) => keyword.trim())
      .filter(Boolean);
    onMetadataSave({
      category: category.trim() || null,
      productArea: productArea.trim() || null,
      searchKeywords: keywords.length > 0 ? keywords : null,
    });
  };

  return (
    <Dialog open={Boolean(event)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-[min(1100px,calc(100vw-2rem))] overflow-y-auto">
        {event ? (
          <>
            <DialogHeader>
              <div className="flex flex-wrap items-center gap-2 pr-8">
                <DialogTitle>{t('admin.feedback.details.title')}</DialogTitle>
                <Badge variant={event.type === 'bad_answer' ? 'secondary' : 'outline'}>
                  {t(`admin.feedback.types.${event.type}`)}
                </Badge>
              </div>
              <DialogDescription>
                {t('admin.feedback.details.created', { date: formatDate(event.createdAt) })}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="min-w-0 space-y-4">
                <DetailBlock title={t('admin.feedback.details.question')} value={event.question} />
                {event.rewrittenQuery ? (
                  <DetailBlock
                    title={t('admin.feedback.details.rewrittenQuery')}
                    value={event.rewrittenQuery}
                  />
                ) : null}
                {event.answerText ? (
                  <DetailBlock
                    title={t('admin.feedback.details.answer')}
                    value={event.answerText}
                  />
                ) : null}
                {event.feedbackText ? (
                  <DetailBlock
                    title={t('admin.feedback.details.feedbackText')}
                    value={event.feedbackText}
                  />
                ) : null}
              </div>

              <aside className="min-w-0 space-y-4 rounded-xl border bg-muted/30 p-4 lg:sticky lg:top-0 lg:self-start">
                <div className="space-y-1.5">
                  <Label>{t('admin.feedback.columns.status')}</Label>
                  <StatusSelect value={event.status} onChange={onStatusChange} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('admin.feedback.columns.assignee')}</Label>
                  <AssigneeSelect
                    users={users}
                    value={event.assignee?.id ?? null}
                    disabled={assigneePending}
                    onChange={onAssigneeChange}
                  />
                </div>
                <MetaRow
                  label={t('admin.feedback.columns.reporter')}
                  value={
                    event.reporter
                      ? userLabel(event.reporter)
                      : t('admin.feedback.reporter.anonymous')
                  }
                />
                <MetaRow
                  label={t('admin.feedback.columns.reason')}
                  value={
                    event.feedbackReason ? t(`admin.feedback.reasons.${event.feedbackReason}`) : '—'
                  }
                />
                <MetaRow
                  label={t('admin.feedback.details.retrievalHitCount')}
                  value={event.retrievalHitCount?.toString() ?? '—'}
                />
                <MetaRow
                  label={t('admin.feedback.details.retrievalTopScore')}
                  value={event.retrievalTopScore ?? '—'}
                />
                {event.sourceDocuments?.length || event.sourceDocumentTitles?.length ? (
                  <Collapsible className="border-t pt-4">
                    <CollapsibleTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        className="group flex h-auto w-full justify-between px-0 py-1 text-left hover:bg-transparent"
                      >
                        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          {t('admin.feedback.details.sourceDocuments')} (
                          {sourceDocumentsForEvent(event).length})
                        </span>
                        <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <ul className="mt-2 list-disc space-y-1 pl-4 text-sm">
                        {sourceDocumentsForEvent(event).map((source) => (
                          <li
                            key={`${source.kbId ?? ''}:${source.documentId ?? ''}:${source.ref ?? ''}:${source.name}`}
                            className="break-words"
                          >
                            <span>{source.name}</span>
                            {source.type || source.kbId || source.documentId || source.ref ? (
                              <dl className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                                {source.type ? (
                                  <SourceMeta label="Type" value={source.type} />
                                ) : null}
                                {source.kbId ? <SourceMeta label="KB" value={source.kbId} /> : null}
                                {source.documentId ? (
                                  <SourceMeta label="Document" value={source.documentId} />
                                ) : null}
                                {source.ref ? <SourceMeta label="Ref" value={source.ref} /> : null}
                              </dl>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </CollapsibleContent>
                  </Collapsible>
                ) : null}
                <div className="space-y-2 border-t pt-4">
                  <Label htmlFor="feedback-category">{t('admin.feedback.details.category')}</Label>
                  <Input
                    id="feedback-category"
                    value={category}
                    onChange={(changeEvent) => setCategory(changeEvent.target.value)}
                    placeholder={t('admin.feedback.details.categoryPlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="feedback-product-area">
                    {t('admin.feedback.details.productArea')}
                  </Label>
                  <Input
                    id="feedback-product-area"
                    value={productArea}
                    onChange={(changeEvent) => setProductArea(changeEvent.target.value)}
                    placeholder={t('admin.feedback.details.productAreaPlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="feedback-keywords">
                    {t('admin.feedback.details.searchKeywords')}
                  </Label>
                  <Input
                    id="feedback-keywords"
                    value={searchKeywords}
                    onChange={(changeEvent) => setSearchKeywords(changeEvent.target.value)}
                    placeholder={t('admin.feedback.details.searchKeywordsPlaceholder')}
                  />
                </div>
                <Button type="button" size="sm" onClick={saveMetadata} disabled={metadataPending}>
                  {t('admin.feedback.details.saveMetadata')}
                </Button>
              </aside>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function DetailBlock({ title, value }: { title: string; value: string }) {
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold text-muted-foreground">{title}</h2>
      <div className="overflow-hidden whitespace-pre-wrap break-words rounded-xl border bg-background/60 p-3 text-sm leading-6 [overflow-wrap:anywhere]">
        {value}
      </div>
    </section>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="break-words text-sm">{value}</p>
    </div>
  );
}

function SourceMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[4.5rem_1fr] gap-2">
      <dt>{label}</dt>
      <dd className="break-all font-mono">{value}</dd>
    </div>
  );
}
